import ContentLoader from 'react-content-loader';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import COLORS from '../../theme/colors';
import Card from '../generic/Card/Card';
import Tooltip from '../generic/Tooltip';

import styles from './IssueOverview.module.less';
import { prettyNumber } from '../../utils/numbers';

type Props = {
  previous: number | null;
  current: number | null;
  isLoading: boolean;
  title: string;
};

const getDeltaText = (delta: number | null): string => {
  if (_.isNil(delta)) {
    return 'No data';
  }

  if (delta === 0) {
    return 'No change';
  }

  let deltaSign = '';
  // The prettyFn already handles negatives for us, so we only need to account for showing +.
  if (delta > 0) {
    deltaSign = '+';
  }

  return `${deltaSign}${prettyNumber(delta)}`;
};

const KPILoader: VoidFunctionComponent = () => (
  <Card>
    <Card.Content>
      <ContentLoader width="100%" height="125px" foregroundColor={COLORS.fossaGray05} backgroundColor={COLORS.fossaGray10}>
        <rect x="0" y="0" rx="3" ry="3" width="75%" height="34px" />
        <rect x="0" y="56" rx="3" ry="3" width="100%" height="34px" />
        <rect x="0" y="103" rx="3" ry="3" width="100%" height="21" />
      </ContentLoader>
    </Card.Content>
  </Card>
);

const KPICard: VoidFunctionComponent<Props> = ({ previous, current, isLoading, title }) => {
  if (isLoading) {
    return <KPILoader />;
  }

  const delta = !_.isNil(current) ? current - (previous ?? 0) : current;

  return (
    <Card variant="fill">
      <Card.Header>{title}</Card.Header>
      <Card.Content>
        <div className={styles.kpiCardCurrent}>
          <Tooltip id="kpi-current-tooltip" tooltip={getDeltaText(delta)}>
            <span>
              {getDeltaText(delta)}
            </span>
          </Tooltip>
        </div>
        <div className={styles.kpiCardPrevious}>
          <Tooltip
            id="kpi-previous-tooltip"
            tooltip={!_.isNil(current) ? prettyNumber(current) : 'n/a'}
            placement="bottom"
          >
            <span>
              <span className={styles.number}>
                {!_.isNil(current) ? prettyNumber(current) : 'n/a'}
              </span>{' '}
              total
            </span>
          </Tooltip>
        </div>
      </Card.Content>
    </Card>
  );
};

export default KPICard;
